import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Star, ArrowRight, Sparkles, TrendingUp,
  LayoutTemplate, ShoppingBag, Users, Zap,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import VerifiedBadge from '../components/shared/VerifiedBadge';
import usePageMeta from '../lib/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const imgUrl = (src) => {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${API_URL}${src}`;
};

const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${
          i <= Math.round(rating)
            ? 'text-amber-400 fill-amber-400'
            : 'text-gray-200 fill-gray-100'
        }`}
      />
    ))}
    {count > 0 && <span className="text-xs text-gray-400 ml-1">({count})</span>}
  </div>
);

const PriceBadge = ({ template }) => {
  const isFree = template.licensing_type === 'free' || !template.price_cents;
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
        isFree
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-violet-100 text-violet-700'
      }`}
    >
      {isFree ? 'Free' : `$${(template.price_cents / 100).toFixed(2)}`}
    </span>
  );
};

const TemplateCard = ({ template, onClick }) => {
  const [imgOk, setImgOk] = useState(true);
  const thumb = imgUrl(template.thumbnail);

  return (
    <div
      onClick={() => onClick(template)}
      className="group cursor-pointer bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      <div className="relative bg-gradient-to-br from-gray-50 to-slate-100" style={{ aspectRatio: '4/3' }}>
        {thumb && imgOk ? (
          <img
            src={thumb}
            alt={template.name}
            className="w-full h-full object-cover"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <LayoutTemplate className="w-10 h-10 text-gray-300" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <PriceBadge template={template} />
        </div>
      </div>
      <div className="p-3">
        <p className="font-medium text-gray-900 text-sm truncate leading-tight">{template.name}</p>
        {template.creator_name && (
          <div className="flex items-center gap-1.5 mt-1">
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0 overflow-hidden">
              {template.creator_avatar
                ? <img src={template.creator_avatar} alt="" className="w-full h-full object-cover" />
                : (template.creator_name[0] || '?').toUpperCase()
              }
            </div>
            <span className="text-xs text-gray-500 truncate">{template.creator_name}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-1.5">
          <StarRating rating={template.average_rating || 0} count={template.review_count || 0} />
          {template.purchases_count > 0 && (
            <span className="text-[11px] text-gray-400">{template.purchases_count} sales</span>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryChip = ({ cat, onClick }) => (
  <button
    onClick={() => onClick(cat)}
    className="flex-shrink-0 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50 transition-all shadow-sm whitespace-nowrap"
  >
    {cat.name}
    {cat.marketplace_template_count > 0 && (
      <span className="ml-1.5 text-xs text-gray-400">{cat.marketplace_template_count}</span>
    )}
  </button>
);

const CreatorCard = ({ creator, onClick }) => (
  <div
    onClick={() => onClick(creator)}
    className="group cursor-pointer bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-4 flex flex-col items-center text-center"
  >
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-lg mb-2">
      {(creator.display_name || '?')[0].toUpperCase()}
    </div>
    <p className="font-semibold text-gray-900 text-sm truncate w-full inline-flex items-center justify-center gap-1">
      {creator.display_name}
      <VerifiedBadge verified={creator.verified} />
    </p>
    <p className="text-xs text-gray-500 mt-0.5">{creator.templates_count} templates</p>
    {creator.average_rating > 0 && (
      <div className="mt-1.5">
        <StarRating rating={creator.average_rating} count={creator.review_count} />
      </div>
    )}
    {creator.available_for_hire && (
      <span className="mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
        For Hire
      </span>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, onViewAll }) => (
  <div className="flex items-end justify-between mb-6">
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-5 h-5 text-violet-600" />
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    {onViewAll && (
      <button
        onClick={onViewAll}
        className="flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-800 transition-colors"
      >
        View all <ArrowRight className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

export default function MarketplacePage() {
  const navigate = useNavigate();
  usePageMeta('Marketplace', 'Templates made and sold by independent creators on iVisora.', { path: '/marketplace' });
  const [searchQuery, setSearchQuery] = useState('');
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [creators, setCreators] = useState([]);
  const [newTemplates, setNewTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [featRes, trendRes, catRes, newRes] = await Promise.all([
          fetch(`${API_URL}/api/marketplace/featured?limit=4`),
          fetch(`${API_URL}/api/marketplace/trending?limit=8`),
          fetch(`${API_URL}/api/marketplace/categories`),
          fetch(`${API_URL}/api/marketplace/new?limit=8`),
        ]);

        const featData = featRes.ok ? await featRes.json() : { items: [] };
        const trendData = trendRes.ok ? await trendRes.json() : { items: [] };
        const catData = catRes.ok ? await catRes.json() : { items: [] };
        const newData = newRes.ok ? await newRes.json() : { items: [] };

        setFeatured(featData.items || []);
        setTrending(trendData.items || []);
        setCategories((catData.items || []).filter((c) => c.marketplace_template_count > 0).slice(0, 20));
        setNewTemplates(newData.items || []);

        // Derive top creators from trending templates
        const allTemplates = [...(featData.items || []), ...(trendData.items || [])];
        const uniqueIds = [...new Set(allTemplates.map((t) => t.creator_id).filter(Boolean))].slice(0, 4);
        if (uniqueIds.length > 0) {
          const profiles = await Promise.all(
            uniqueIds.map((id) =>
              fetch(`${API_URL}/api/marketplace/creators/${id}`)
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
            )
          );
          setCreators(profiles.filter(Boolean));
        }
      } catch (err) {
        console.error('Marketplace load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/marketplace/browse${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`);
  };

  const handleTemplateClick = (t) => navigate(`/marketplace/templates/${t.id}`);
  const handleCreatorClick = (c) => navigate(`/marketplace/creators/${c.id}`);
  const handleCategoryClick = (c) => navigate(`/marketplace/browse?category_id=${c.id}`);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-violet-950 to-fuchsia-950 text-white py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm mb-6">
            <ShoppingBag className="w-3.5 h-3.5 text-fuchsia-300" />
            <span className="text-white/80">Template Marketplace</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-4">
            Discover{' '}
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              Premium
            </span>{' '}
            Templates
          </h1>
          <p className="text-lg text-white/70 mb-8">
            Professionally designed templates created by top designers. Free and paid.
          </p>
          <form onSubmit={handleSearch} className="flex gap-3 max-w-xl mx-auto">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search templates…"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/95 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 shadow-lg"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold text-sm hover:opacity-90 transition-opacity shadow-lg whitespace-nowrap"
            >
              Browse All
            </button>
          </form>
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-white/50">
            <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-fuchsia-400" /> Instant use</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-violet-400" /> Creator marketplace</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-amber-400" /> Free &amp; paid</span>
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Featured */}
            {featured.length > 0 && (
              <section className="mb-14">
                <SectionHeader
                  icon={Sparkles}
                  title="Featured Templates"
                  subtitle="Hand-picked by our team"
                  onViewAll={() => navigate('/marketplace/browse?sort=newest')}
                />
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {featured.map((t) => (
                    <TemplateCard key={t.id} template={t} onClick={handleTemplateClick} />
                  ))}
                </div>
              </section>
            )}

            {/* Trending */}
            {trending.length > 0 && (
              <section className="mb-14">
                <SectionHeader
                  icon={TrendingUp}
                  title="Trending Now"
                  subtitle="Most popular this week"
                  onViewAll={() => navigate('/marketplace/browse?sort=trending')}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {trending.slice(0, 8).map((t) => (
                    <TemplateCard key={t.id} template={t} onClick={handleTemplateClick} />
                  ))}
                </div>
              </section>
            )}

            {/* Categories */}
            {categories.length > 0 && (
              <section className="mb-14">
                <SectionHeader
                  icon={LayoutTemplate}
                  title="Browse by Category"
                  onViewAll={() => navigate('/marketplace/browse')}
                />
                <div className="flex flex-wrap gap-2.5">
                  {categories.map((cat) => (
                    <CategoryChip key={cat.id} cat={cat} onClick={handleCategoryClick} />
                  ))}
                </div>
              </section>
            )}

            {/* Top Creators */}
            {creators.length > 0 && (
              <section className="mb-14">
                <SectionHeader
                  icon={Users}
                  title="Top Creators"
                  subtitle="Talented designers on our platform"
                />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {creators.map((c) => (
                    <CreatorCard key={c.id} creator={c} onClick={handleCreatorClick} />
                  ))}
                </div>
              </section>
            )}

            {/* New Templates */}
            {newTemplates.length > 0 && (
              <section className="mb-14">
                <SectionHeader
                  icon={Sparkles}
                  title="New Templates"
                  subtitle="Just added to the marketplace"
                  onViewAll={() => navigate('/marketplace/browse?sort=newest')}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {newTemplates.map((t) => (
                    <TemplateCard key={t.id} template={t} onClick={handleTemplateClick} />
                  ))}
                </div>
              </section>
            )}

            {/* Become a creator CTA */}
            <section className="rounded-3xl bg-gradient-to-br from-violet-600 to-fuchsia-700 p-10 text-center text-white">
              <h2 className="text-2xl font-bold mb-2">Are you a designer?</h2>
              <p className="text-white/75 mb-6 max-w-md mx-auto">
                List your templates on the marketplace and earn 70% on every sale.
              </p>
              <button
                onClick={() => navigate('/creator/become')}
                className="px-6 py-3 rounded-xl bg-white text-violet-700 font-semibold text-sm hover:bg-violet-50 transition-colors shadow"
              >
                Start Selling →
              </button>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
